import React, { useState } from 'react'
import { Button, Icon } from '@shopflo/ui'
import { Plus, PlusCircle, Stack } from '@phosphor-icons/react'
import { CatalogueDataType } from 'src/lib/types/common/catalogue'
import ProductSearchDialog from 'src/components/common/product-selector/ProductSearchDialog'
import CatalogueItemsGroup from './CatalogueItemsGroup'
import { useLocale } from 'src/lib/hooks/useLocal'
import { QuantityDistributionErrorType } from 'src/lib/utils/automationsHelpers'
import { ProductSelectorViewMode } from 'src/components/common/product-selector/lib/productSelectorTypes'

interface CatalogueGroupControlProps {
  groupIndex: number
  includedGroups: CatalogueDataType[][]
  excludedGroups?: CatalogueDataType[][]
  catalogueType: 'COLLECTION' | 'PRODUCT_AND_VARIANT'

  actions: {
    onRemoveIncludedItem: (groupIndex: number, itemIndex: number) => void
    onRemoveExcludedItem?: (groupIndex: number, itemIndex: number) => void
    onUpdateItem?: (updatedItem: CatalogueDataType, groupIndex: number, itemIndex: number) => void
    onAddEmptyGroup?: () => void
    onItemsSelected: (products: CatalogueDataType[], excludedProducts: CatalogueDataType[], groupIndex: number) => void
  }

  errorHandling: {
    errors?: QuantityDistributionErrorType[]
  }

  config?: {
    showVariantEditor?: boolean
    addText?: string
    emptyText?: string
    emptyTextClassName?: string
    showExclusionOption?: boolean
    shouldShowAllProducts?: boolean
  }
  renderItemPrefix?: (item: CatalogueDataType, itemIndex: number) => React.ReactNode
}

const CatalogueGroupControl: React.FC<CatalogueGroupControlProps> = ({
  groupIndex,
  includedGroups,
  excludedGroups,
  catalogueType,
  actions,
  errorHandling,
  config = { showVariantEditor: true, showExclusionOption: true, shouldShowAllProducts: true },
  renderItemPrefix
}) => {
  const { t } = useLocale()
  const [isSelectorOpen, setIsSelectorOpen] = useState(false)
  const includedGroup = includedGroups[groupIndex] || []
  const excludedGroup = excludedGroups?.[groupIndex] || []
  const isLastGroup = groupIndex === includedGroups.length - 1

  const displayGroup = [...includedGroup, ...excludedGroup.map(item => ({ ...item, type: 'exclude' }))]

  const handleApply = (products: CatalogueDataType[] | undefined | null, excluded?: CatalogueDataType[]) => {
    if (products) {
      actions.onItemsSelected(products, excluded || [], groupIndex)
    }
    setIsSelectorOpen(false)
  }

  return (
    <div className='flex flex-col rounded-lg w-full'>
      <CatalogueItemsGroup
        groupIndex={groupIndex}
        displayGroup={displayGroup}
        actions={actions}
        errorHandling={errorHandling}
        config={config}
        renderItemPrefix={renderItemPrefix}
      />

      {displayGroup.length > 0 ? (
        <div className='flex gap-2 py-2 mb-2'>
          <Button
            id={`add-item-button-group-${groupIndex}`}
            variant='dashed'
            leftIcon={PlusCircle}
            onClick={() => setIsSelectorOpen(true)}
            className='bg-transparent h-7 w-fit text-text-sm-medium'
            size='sm'
          >
            {catalogueType === 'COLLECTION' ? t('discounts_v2.add_collection') : t('discounts_v2.add_product_label')}
          </Button>

          {isLastGroup && actions.onAddEmptyGroup && (
            <Button
              id={`add-group-button-group-${groupIndex}`}
              variant='dashed'
              leftIcon={Stack}
              size='sm'
              onClick={actions.onAddEmptyGroup}
              className='bg-transparent h-7 w-fit text-text-sm-medium'
            >
              {t('discounts_v2.add_group')}
            </Button>
          )}
        </div>
      ) : (
        <div
          id={`empty-group-trigger-group-${groupIndex}`}
          className='cursor-pointer flex gap-2 items-center py-3'
          onClick={() => setIsSelectorOpen(true)}
        >
          <div className='flex items-center justify-center gap-2 w-8 h-8 border-1 border-dashed border-zinc-300 rounded-lg'>
            <Icon IconComponent={Plus} size='xs' className='text-zinc-400' />
          </div>
          <div className='flex gap-2'>
            <p className={config.emptyTextClassName || 'text-text-sm-medium text-zinc-500'}>
              {config.emptyText ||
                (catalogueType === 'COLLECTION'
                  ? t('discounts_v2.choose_collections')
                  : t('discounts_v2.choose_products'))}
            </p>
          </div>
        </div>
      )}

      <ProductSearchDialog
        openHandler={{
          open: isSelectorOpen,
          onOpenChange: setIsSelectorOpen
        }}
        actionHandler={{
          onApply: handleApply
        }}
        products={{
          initialSelectedProducts: includedGroup,
          initialExcludedProducts: excludedGroup
        }}
        catalogueType={catalogueType}
        viewModeHandler={{
          defaultViewMode: ProductSelectorViewMode.SELECT,
          showExclusionOption: config.showExclusionOption,
          showAllOption: config.shouldShowAllProducts
        }}
      />
    </div>
  )
}

export default CatalogueGroupControl
